import type { ReactNode } from 'react';

// A compact summary row for dashboard widgets: an icon chip, a text column
// (optional green eyebrow, title, optional subtitle), and an optional trailing slot.
type SummaryRowProps = {
  icon: ReactNode;
  /** Classes for the icon chip; override to tint it (default: white chip). */
  iconClassName?: string;
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Let the subtitle wrap to multiple lines instead of truncating to one. */
  subtitleWrap?: boolean;
  trailing?: ReactNode;
};

export function SummaryRow({
  icon,
  iconClassName = 'bg-surface text-slate-500',
  eyebrow,
  title,
  subtitle,
  subtitleWrap = false,
  trailing,
}: SummaryRowProps) {
  return (
    <div
      data-testid="summary-row"
      className="flex items-center gap-3.5 rounded-xl bg-slate-50 p-3.5"
    >
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${iconClassName}`}
      >
        {icon}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {eyebrow ? (
          <p className="text-xs font-semibold text-brand-hover">{eyebrow}</p>
        ) : null}
        <p className="truncate text-sm font-semibold text-slate-900">{title}</p>
        {subtitle ? (
          <p className={`text-xs text-slate-500 ${subtitleWrap ? '' : 'truncate'}`}>{subtitle}</p>
        ) : null}
      </div>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </div>
  );
}
